import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography } from "@mui/material";
import { CloseIcon, InfoIcon } from "../../../components/Icons";
import React, { useState } from "react";

import { openExternalLink } from "../../../utils";
import { useDismissVetCoveRedirect } from
  "../../../services/dismiss-vetcove-redirect/dismiss-vetcove-redirect.service";
import { useDispatch } from "react-redux";
import { useModal } from "../../../context/ModalContext";
import { useUserStore } from "../../../hooks/redux-hooks/user-store/user.store";
import { userActions } from "../../../state/user";

const COPY = {
  TITLE: "You're Being Redirected to VetCove",
  DESCRIPTION:
    "VetCove powers our online pharmacy. To continue, " +
    "you will need to create a VetCove account - " +
    "it only takes a moment.",
  IMPORTANT_HEADING: "Important: Match Your GoodVets Email or Phone Number",
  IMPORTANT_BODY:
    "Use the same email address or phone number " +
    "that you use to login to your GoodVets app. " +
    "This links your accounts as your pet's records " +
    "and order history stay in sync.",
  CHECKBOX_LABEL: "Don't show this message again",
  CONTINUE: "Continue",
} as const;

const VetCoveRedirectModal = () => {
  const { closeModal, params } = useModal();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { dismissVetCoveRedirect, loading } = useDismissVetCoveRedirect();
  const { user } = useUserStore();
  const dispatch = useDispatch();
  const actionUrl = (params?.actionUrl as string) || "";

  const handleClose = () => {
    closeModal();
  };


  const handleContinue = async () => {
    openExternalLink(actionUrl);
    if (dontShowAgain) {
      await dismissVetCoveRedirect();
      if (user) {
        dispatch(userActions.setUserProfile({ ...user, isVetCoveRedirectDismissed: true }));
      }
    }
    closeModal();
  };

  return (
    <div className="flex flex-col w-full xl:w-[33rem] max-h-[85vh]">
      <div className="flex justify-end cursor-pointer shrink-0" onClick={handleClose}>
        <CloseIcon />
      </div>

      <div className="flex flex-col items-center gap-5 px-6 pb-4 overflow-y-auto scrollbar-thin">
        <Box
          className="flex items-center justify-center rounded-full shrink-0"
          sx={{
            width: 72,
            height: 72,
            backgroundColor: "torquise.light",
          }}
        >
          <InfoIcon width={32} height={32} color="#537561" />
        </Box>

        <Typography
          variant="h1"
          className="w-full"
          sx={{ fontSize: { xs: "24px", sm: "32px" } }}
        >
          {COPY.TITLE}
        </Typography>

        <Typography
          variant="body1"
          className="w-full"
          sx={{ color: "gray.main", fontSize: "16px" }}
        >
          {COPY.DESCRIPTION}
        </Typography>

        <Box
          className="w-full rounded-lg py-6 px-5"
          sx={{ backgroundColor: "primary.light" }}
        >
          <Typography
            fontWeight={700}
            sx={{ mb: 1, fontSize: "15px" }}
          >
            {COPY.IMPORTANT_HEADING}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "gray.dark" }}>
            {COPY.IMPORTANT_BODY}
          </Typography>
        </Box>

        <Box
          className="w-full rounded-lg py-2 px-2"
          sx={{ backgroundColor: "sand.main" }}
        >
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                sx={{
                  "&.Mui-checked": {
                    color: "primary.main",
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: "14px" }}>
                {COPY.CHECKBOX_LABEL}
              </Typography>
            }
          />
        </Box>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={loading}
          sx={{
            fontWeight: 600,
            height: "48px",
            fontSize: "16px",
            borderRadius: "24px",
          }}
          onClick={handleContinue}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : COPY.CONTINUE}
        </Button>
      </div>
    </div>
  );
};

export default VetCoveRedirectModal;
